export function isParseFailure(result) {
    return 'error' in result;
}
const SECTIONS = {
    '5G (SA) Messergebnis': '5G',
    '4G (LTE) Messergebnis': '4G',
    'LTE-M Messergebnis': 'LTE-M',
};
const REQUIRED_COLUMNS = ['MCC', 'MNC', 'Zellen-ID', 'RSRP', 'RSRQ', 'Netzwerkname'];
function cleanField(field) {
    const trimmed = field.trim();
    return trimmed.startsWith("'") ? trimmed.slice(1).trim() : trimmed;
}
function splitFields(line) {
    return line.split(',').map(cleanField);
}
function toNumber(value) {
    if (value === undefined || value.trim() === '')
        return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}
function normaliseHeader(header) {
    return header.replace(/^AV\s+/, '').trim();
}
const CREATED_PATTERN = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})$/;
const FILE_NAME_PATTERN = /^(\d{4}-\d{2}-\d{2})_(\d{2})-(\d{2})-(\d{2})__/;
function measuredAtFrom(created, fileName) {
    const fromHeader = created ? CREATED_PATTERN.exec(created) : null;
    if (fromHeader)
        return `${fromHeader[1]}T${fromHeader[2]}`;
    const fromName = FILE_NAME_PATTERN.exec(fileName);
    if (fromName)
        return `${fromName[1]}T${fromName[2]}:${fromName[3]}:${fromName[4]}`;
    return undefined;
}
export function parseSnyperReport(fileName, content) {
    const text = content.charCodeAt(0) === 0xfeff ? content.slice(1) : content;
    const lines = text.split(/\r?\n/);
    if (!lines.slice(0, 3).some((line) => line.trim() === 'Siretta Limited')) {
        return { fileName, error: 'Die Datei ist kein Bericht des SNYPER-Messgeräts.' };
    }
    const meta = new Map();
    const cells = [];
    const warnings = [];
    let technology;
    let header;
    let sectionCount = 0;
    for (const rawLine of lines) {
        const line = rawLine.trim();
        if (line === '' || /^=+$/.test(line))
            continue;
        const sectionTechnology = SECTIONS[line];
        if (sectionTechnology) {
            technology = sectionTechnology;
            header = undefined;
            sectionCount += 1;
            continue;
        }
        if (line.endsWith('Zusammenfassung')) {
            technology = undefined;
            header = undefined;
            continue;
        }
        if (!technology) {
            const [key, ...rest] = splitFields(rawLine);
            if (key)
                meta.set(key, rest.join(','));
            continue;
        }
        if (!header) {
            header = splitFields(rawLine).map(normaliseHeader);
            const missing = REQUIRED_COLUMNS.filter((column) => !header?.includes(column));
            if (missing.length > 0) {
                return {
                    fileName,
                    error: `Im Abschnitt ${technology} fehlen Spalten: ${missing.join(', ')}.`,
                };
            }
            continue;
        }
        const fields = splitFields(rawLine);
        if (!/^\d+$/.test(fields[0] ?? '')) {
            warnings.push(`Unerwartete Zeile im Abschnitt ${technology} übersprungen.`);
            continue;
        }
        const columns = header;
        const column = (name) => {
            const index = columns.indexOf(name);
            return index < 0 ? undefined : fields[index];
        };
        const provider = column('Netzwerkname') ?? '';
        if (provider === '') {
            warnings.push(`Zeile ohne Netzwerkname im Abschnitt ${technology} übersprungen.`);
            continue;
        }
        const pci = toNumber(column('PCI'));
        const channel = toNumber(column('EARFCN') ?? column('NR-ARFCN'));
        const band = column('Band');
        cells.push({
            technology,
            provider,
            mcc: column('MCC') ?? '',
            mnc: column('MNC') ?? '',
            cellId: column('Zellen-ID') ?? '',
            ...(pci !== null ? { pci } : {}),
            ...(band ? { band } : {}),
            ...(channel !== null ? { channel } : {}),
            rsrp: toNumber(column('RSRP')),
            rsrq: toNumber(column('RSRQ')),
            sinr: toNumber(column('SINR')),
            dbm: toNumber(column('dBm')),
        });
    }
    if (sectionCount === 0) {
        return { fileName, error: 'Die Datei enthält keine Messergebnisse.' };
    }
    const measuredAt = measuredAtFrom(meta.get('Datei erstellt'), fileName);
    if (!measuredAt) {
        return {
            fileName,
            error: 'Der Messzeitpunkt lässt sich weder aus der Datei noch aus dem Namen lesen.',
        };
    }
    const cyclesMatch = /^(\d+)\/(\d+)$/.exec(meta.get('Wiederholungen') ?? '');
    if (!cyclesMatch)
        warnings.push('Anzahl der Wiederholungen fehlt, angenommen wird 1/1.');
    const cycles = cyclesMatch
        ? { completed: Number(cyclesMatch[1]), target: Number(cyclesMatch[2]) }
        : { completed: 1, target: 1 };
    return { fileName, measuredAt, cycles, cells, warnings };
}
/** Measuring point numbers from 1 per series, by device time, then file name (answer 2.5). */
export function assignSequence(reports) {
    return [...reports]
        .sort((a, b) => a.measuredAt === b.measuredAt
        ? a.fileName.localeCompare(b.fileName)
        : a.measuredAt < b.measuredAt
            ? -1
            : 1)
        .map((report, index) => ({ ...report, sequenceNumber: index + 1 }));
}
//# sourceMappingURL=snyper-report.js.map